/**
 * Referral Utility
 * 
 * Handles capturing, persisting, and retrieving referral codes from URLs.
 */

const REFERRAL_STORAGE_KEY = 'guardian_referral_code';
const REFERRAL_EXPIRY_DAYS = 30;

/**
 * Capture referral code from URL query parameters and save to localStorage.
 * Should be called at the root of the application.
 */
export function captureReferralParam(): string | null {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');

  if (refCode) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + REFERRAL_EXPIRY_DAYS);
    
    const referralData = {
      code: refCode,
      capturedAt: new Date().toISOString(),
      expiresAt: expiryDate.toISOString(),
      sourceUrl: window.location.href
    };

    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
    console.log(`[Referral] Captured code: ${refCode}`);
    return refCode;
  }

  return null;
}

/**
 * Retrieve the active referral code from localStorage.
 * Checks for expiration before returning.
 */
export function getReferralCode(): string | null {
  if (typeof window === 'undefined') return null;

  const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
  if (!stored) return null;

  try {
    const data = JSON.parse(stored);
    const expiresAt = new Date(data.expiresAt);
    
    if (new Date() > expiresAt) {
      console.log('[Referral] Stored code expired.');
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }

    return data.code;
  } catch (e) {
    console.error('[Referral] Failed to parse stored referral data', e);
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    return null;
  }
}

/**
 * Clear the stored referral code.
 * Usually called after a successful conversion.
 */
export function clearReferralCode(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(REFERRAL_STORAGE_KEY);
}
